import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Theme, useMediaQuery } from '@mui/material';
import { CoreBox, CoreTypography } from '@youscience/core';

import ActionButton from '@components/ActionButton';

import { CollegeLocation } from '@interfaces/location';

import useSchoolFavoritesStore from '@store/SchoolFavoritesStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';
import useUserStore from '@store/UserStore';
import useAuthStore, { ProfileWithExtendedData } from '@store/AuthStore';

import { getBreadcrumbs } from '@utils/breadcrumbs';
import { isAlreadyGraduated } from '@utils/studentProgress';

import { AllSchoolConnectionModalProps, withAllSchoolConnectionModal } from '@HOCs/withAllSchoolConnectionModal';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { ROUTES } from '@constants/ROUTES';
import { MIN_OPT_IN_USER_AGE, MIN_OPT_IN_USER_GRADE } from '@constants/optIn';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Layout/components/Container';
import { CardViewSwitcher } from '@components/CardViewSwitcher';
import { SchoolCard } from '@components/SchoolCard';
import { EmptyStateText } from '@components/EmptyStateText';

import FilledHeartIcon from '@images/heart-filled.svg?react';
import MediumTargetIcon from '@images/medium-target.svg?react';

import {
  sxStyles,
  StyledNavBar,
  StyledActionPanel,
  StyledNavBarTitle,
  StyledExploreByTitle,
  StyledMajorsLink,
  StyledSchoolsLink,
  StyledSwitcher,
  StyledSwitcherWrap,
  StyledFavoritesLink,
  StyledCardViewSwitcherWrap,
  StyledSchoolCardsWrap,
} from './FavoriteSchoolList.styles';

// eslint-disable-next-line
interface FavoriteSchoolListProps extends AllSchoolConnectionModalProps {}

const FavoriteSchoolList = (props: FavoriteSchoolListProps) => {
  const { onOpenAllSchoolConnection } = props;

  const location: CollegeLocation = useLocation();
  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [isLinearView, setIsLinearView] = useState(false);

  const schoolFavorites = useUserPreferencesStore((state) => state.schoolFavorites);
  const isLoading = useSchoolFavoritesStore((state) => state.isLoading);

  const { userData } = useAuthStore((state) => state.authSession);
  const { gradeLevel } = (userData?.profile ?? {}) as ProfileWithExtendedData;

  const age = useUserStore.getState().getUserAge();
  const alreadyGraduated = isAlreadyGraduated();

  const showManageSharing =
    age >= MIN_OPT_IN_USER_AGE && ((gradeLevel || 0) >= MIN_OPT_IN_USER_GRADE || alreadyGraduated);

  const pageBreadcrumbs = getBreadcrumbs(location.state?.from, BREADCRUMBS_PAGE_TEXT.FAVORITE_SCHOOLS);

  return (
    <CoreBox sx={sxStyles.schoolsBg}>
      <CoreBox sx={sxStyles.headSectionWrap}>
        <Container sx={sxStyles.headSectionInner}>
          <Breadcrumbs links={pageBreadcrumbs.links} currentPage={pageBreadcrumbs.currentPage} />

          <StyledNavBar>
            <CoreBox sx={sxStyles.navBarInfo}>
              <StyledNavBarTitle variant="h2" component="h1">
                Favorites
              </StyledNavBarTitle>

              <CoreTypography>
                Your talents are needed! Put your natural talents to work by checking out these awesome majors and
                programs.
              </CoreTypography>
            </CoreBox>

            <StyledSwitcherWrap>
              <StyledExploreByTitle component="div">Explore by:</StyledExploreByTitle>

              <StyledSwitcher>
                <StyledSchoolsLink to={ROUTES.FAVORITE_SCHOOLS}>SCHOOLS</StyledSchoolsLink>

                <StyledMajorsLink
                  to={ROUTES.FAVORITE_MAJORS}
                  state={{
                    from: [{ to: ROUTES.MAJORS, text: BREADCRUMBS_PAGE_TEXT.MAJORS }],
                  }}
                >
                  MAJORS
                </StyledMajorsLink>
              </StyledSwitcher>
            </StyledSwitcherWrap>
          </StyledNavBar>

          <StyledActionPanel>
            <StyledFavoritesLink
              to={ROUTES.SCHOOLS}
              state={{
                from: [{ to: ROUTES.FAVORITE_SCHOOLS, text: BREADCRUMBS_PAGE_TEXT.FAVORITE_SCHOOLS }],
              }}
            >
              <FilledHeartIcon />
              Favorites ({schoolFavorites?.length || 0})
            </StyledFavoritesLink>

            {schoolFavorites.length > 0 && showManageSharing ? (
              <ActionButton onClick={onOpenAllSchoolConnection}>Manage sharing</ActionButton>
            ) : null}
          </StyledActionPanel>
        </Container>
      </CoreBox>

      <Container sx={sxStyles.mainSectionWrap}>
        {schoolFavorites.length > 0 && (
          <StyledCardViewSwitcherWrap>
            <CardViewSwitcher isLinearView={isLinearView} setIsLinearView={setIsLinearView} />
          </StyledCardViewSwitcherWrap>
        )}

        <StyledSchoolCardsWrap>
          {schoolFavorites.map((schoolFavorite) => {
            const { school } = schoolFavorite;

            if (isLinearView && !isMobileScreen) {
              return (
                <SchoolCard
                  key={school.id}
                  school={school}
                  schoolCardType="linear"
                  componentId="favorite-school-list-card"
                />
              );
            }

            return (
              <SchoolCard
                key={school.id}
                school={school}
                schoolCardType="reduced"
                componentId="favorite-school-list-card"
              />
            );
          })}
        </StyledSchoolCardsWrap>
      </Container>

      {!isLoading && schoolFavorites.length === 0 && (
        <EmptyStateText
          icon={<MediumTargetIcon />}
          title="No favorite schools yet"
          subtitle="Hmmm. Looks like you haven't favorited any schools."
          buttonTitle="Start exploring"
          buttonLink={ROUTES.SCHOOLS}
        />
      )}
    </CoreBox>
  );
};

export const FavoriteSchoolListWithModal = withAllSchoolConnectionModal(FavoriteSchoolList);
